import { Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtomValue, useSetAtom } from "jotai";
import classNames from "classnames";
import {
  isAgentsEnabledAtom,
  selectedNodeTypeAtom,
  formsTerminologyAtom,
  selectNodeTypeSetter,
} from "@zapier/canvas-state";

import styles from "./DiagrammingToolbar.module.css";

type Props = {
  isDisabled: boolean;
};

const logoNames = {
  canvasLogoName: "navCanvas",
  tablesLogoName: "navTables",
  interfacesLogoName: "navInterfaces",
  chatbotsLogoName: "navAIChatbot",
  agentsLogoName: "navCentral",
} as const;

export function AppNodeOptions({ isDisabled }: Props) {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const isAgentsEnabled = useAtomValue(isAgentsEnabledAtom);
  const maybeSelectNodeType = useSetAtom(selectNodeTypeSetter);

  const {
    tablesLogoName,
    interfacesLogoName,
    chatbotsLogoName,
    agentsLogoName,
  } = logoNames;
  const listItemClass = isDisabled
    ? `${styles.dndnode} ${styles.dndnodeDisabled}`
    : styles.dndnode;

  return (
    <ul className={styles.horizontalList}>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "zap",
        })}
      >
        <Tooltip placement="top" content={isDisabled ? "" : "Zap"}>
          <TooltipFocusableTrigger>
            <button
              aria-label="Add Zap step"
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("zap");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("zap");
              }}
            >
              <Icon size={28} color="BrandOrange" name="miscBoltAltFill" />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "table",
        })}
      >
        <Tooltip placement="top" content={isDisabled ? "" : "Table"}>
          <TooltipFocusableTrigger>
            <button
              aria-label="Add table step"
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("table");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("table");
              }}
            >
              <Icon size={28} color="BrandOrange" name={tablesLogoName} />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "interface",
        })}
      >
        <Tooltip
          placement="top"
          content={isDisabled ? "" : interfacesTerms.singular}
        >
          <TooltipFocusableTrigger>
            <button
              aria-label={`Add ${interfacesTerms.singularLower} step`}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("interface");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("interface");
              }}
            >
              <Icon size={28} color="BrandOrange" name={interfacesLogoName} />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "chatbots",
        })}
      >
        <Tooltip placement="top" content={isDisabled ? "" : "Chatbot"}>
          <TooltipFocusableTrigger>
            <button
              aria-label="Add chatbots step"
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("chatbots");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("chatbots");
              }}
              draggable={!isDisabled}
            >
              <Icon size={28} color="BrandOrange" name={chatbotsLogoName} />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      </li>
      {isAgentsEnabled && (
        <li
          className={classNames(listItemClass, {
            [styles.dndnodeSelected]: selectedNodeType === "agent",
          })}
        >
          <Tooltip placement="top" content={isDisabled ? "" : "Agent"}>
            <TooltipFocusableTrigger>
              <button
                aria-label="Add agent step"
                className={styles.dndButton}
                disabled={isDisabled}
                onClick={() => maybeSelectNodeType("agent")}
                onKeyDown={() => maybeSelectNodeType("agent")}
              >
                <Icon size={28} color="BrandOrange" name={agentsLogoName} />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        </li>
      )}
    </ul>
  );
}
